import type { MaybeRefOrGetter } from 'vue'
import { parseInstant, serverNow } from '~/utils/time'

/** 目标没变（服务端还没翻篇）时补发到点回调的次数与间隔 */
const MAX_REFIRES = 3
const REFIRE_DELAY_MS = 5000

export interface CountdownOptions {
  /**
   * 到点回调：调用方据此**重新拉接口**，不在本地直接改状态（规范 6.2）。
   * 同一目标只触发一次；重拉后目标没变（服务端时钟略慢、还没翻篇）时每 5 秒补发，最多 3 次。
   */
  onExpire?: () => void
}

/**
 * 绝对时刻倒计时（只在客户端运行）。
 *
 * - 目标是接口给的时刻串（reset_at / next_at / expire_at / next_settle_at / expires_at …）或毫秒；
 * - 每一跳都按「目标 − serverNow()」重算，不做递减：锁屏、后台节流、设备时钟快慢都不会累积误差；
 * - 切回前台（visibilitychange）立即重算；
 * - remaining 为 null 表示未运行（服务端渲染、挂载前、没有目标），界面显示占位。
 *
 * 注：同名的 VueUse useCountdown（按秒递减）优先级更低，被本实现覆盖。
 */
export function useCountdown(
  target: MaybeRefOrGetter<string | number | null | undefined>,
  options: CountdownOptions = {},
) {
  const targetMs = computed<number | null>(() => {
    const value = toValue(target)
    if (typeof value === 'number') return Number.isFinite(value) ? value : null
    return parseInstant(value ?? null)
  })
  const remaining = ref<number | null>(null)

  let timer: ReturnType<typeof setTimeout> | null = null
  let refireTimer: ReturnType<typeof setTimeout> | null = null
  let expiredFor: number | null = null
  let refires = 0
  let active = false

  const clear = () => {
    if (timer !== null) clearTimeout(timer)
    timer = null
  }

  const clearRefire = () => {
    if (refireTimer !== null) clearTimeout(refireTimer)
    refireTimer = null
  }

  const fireExpire = (at: number) => {
    options.onExpire?.()
    if (refires >= MAX_REFIRES) return
    refireTimer = setTimeout(() => {
      refireTimer = null
      if (!active || targetMs.value !== at) return
      refires += 1
      fireExpire(at)
    }, REFIRE_DELAY_MS)
  }

  const tick = () => {
    clear()
    if (!active) return
    const at = targetMs.value
    if (at === null) {
      clearRefire()
      remaining.value = null
      return
    }
    const left = at - serverNow()
    if (left > 0) {
      clearRefire()
      remaining.value = left
      // 下一次显示变化的时刻（秒向上取整，所以在跨过整秒时翻页），多等 16ms 保证越过边界
      timer = setTimeout(tick, (left % 1000 || 1000) + 16)
      return
    }
    remaining.value = 0
    if (expiredFor === at) return
    clearRefire()
    expiredFor = at
    refires = 0
    fireExpire(at)
  }

  if (import.meta.client) {
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick()
    }
    onMounted(() => {
      active = true
      document.addEventListener('visibilitychange', onVisible)
      tick()
    })
    onBeforeUnmount(() => {
      active = false
      document.removeEventListener('visibilitychange', onVisible)
      clear()
      clearRefire()
    })
    watch(targetMs, () => tick())
  }

  const expired = computed(() => remaining.value === 0)
  const running = computed(() => remaining.value !== null)

  return { targetMs, remaining, expired, running }
}
